import type { QuestionnaireItem } from '@medplum/fhirtypes'
import type { ExtendedQuestionnaireResponse } from '@seen/core/models/questionnaireResponse'
import * as questionnaires from '@seen/core/models/questionnaires/index'

import { Button, cn, Label } from '@seen/ui'
import { IconPencil } from '@tabler/icons-react'
import { compact } from 'lodash-es'
import { type JSX, useState } from 'react'
import { match } from 'ts-pattern'
import { ComboboxButton } from '#src/components/ComboboxButton.tsx'
import {
  COMBOBOX_UNDEFINED_VALUE,
  getAnswerOptions,
} from '#src/components/core/combobox/util.ts'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogButton } from '#src/components/core/dialog/DialogButton.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

export function QuestionnaireItemForm({
  item,
  questionnaireResponse,
  label,
  className,
  formClassName,
  placeholder,
  onSaved,
}: {
  item: QuestionnaireItem
  questionnaireResponse: ExtendedQuestionnaireResponse | null
  label?: string
  className?: string
  formClassName?: string
  placeholder?: string
  // Runs after each answer is saved.
  onSaved?: () => void
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const isReadOnly = useIsReadOnly()

  if (questionnaires.isSensitiveQuestionnaireItem(item)) {
    const value = questionnaireResponse?.getAnswerValue(item.linkId)
    const displayValue = typeof value === 'string' ? value : ''

    return (
      <div className={cn('grid', className)}>
        {!!label && <Label>{label}</Label>}
        <div className="group flex items-center gap-1">
          <span className="flex-1 text-muted-foreground">{displayValue}</span>
          {!isReadOnly && (
            <div className="invisible flex group-hover:visible">
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogButton variant="ghost" size="icon">
                  <IconPencil className="size-4" />
                </DialogButton>
                <DialogDisplay>
                  <DialogHeader>
                    <DialogTitle>Edit {label}</DialogTitle>
                  </DialogHeader>
                  <DialogContent>
                    <QuestionnaireItemFormContent
                      item={item}
                      questionnaireResponse={questionnaireResponse}
                      label={label}
                      className={className}
                      formClassName={formClassName}
                      placeholder={placeholder}
                      onSaved={onSaved}
                    />
                  </DialogContent>
                </DialogDisplay>
              </Dialog>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <QuestionnaireItemFormContent
      item={item}
      questionnaireResponse={questionnaireResponse}
      label={label}
      className={className}
      formClassName={formClassName}
      placeholder={placeholder}
      onSaved={onSaved}
    />
  )
}

function QuestionnaireItemFormContent({
  item,
  questionnaireResponse,
  label,
  className,
  formClassName,
  placeholder,
  onSaved,
}: {
  item: QuestionnaireItem
  questionnaireResponse: ExtendedQuestionnaireResponse | null
  label?: string
  className?: string
  formClassName?: string
  placeholder?: string
  // Runs after each answer is saved.
  onSaved?: () => void
}): JSX.Element {
  const mutationOptions = onSaved ? { onSuccess: () => onSaved() } : undefined

  if (item.type === 'text' || item.type === 'string') {
    return (
      <ResourceFieldForm
        className={formClassName}
        instance={questionnaireResponse}
        mutationOptions={mutationOptions}
        valueFn={(qr) =>
          (qr.getAnswerValue(item.linkId) ?? null) as string | undefined
        }
        patchFn={(qr, value) => {
          qr.setAnswer(item, value ?? undefined)
        }}
        debounceTyping
        render={({ field: { disabled, ...rest } }) => (
          <div className={cn('grid', className)}>
            {!!label && (
              <Label
                className={cn('pb-1', {
                  'cursor-not-allowed': disabled,
                })}
              >
                {label}
              </Label>
            )}
            <RichTextEditor
              variant="underline"
              disabled={disabled}
              className={cn({ 'pt-0': !!label })}
              placeholder={placeholder}
              related={compact([questionnaireResponse?.reference])}
              {...rest}
            />
          </div>
        )}
      />
    )
  }

  if (item.type === 'choice') {
    return (
      <ResourceFieldForm
        instance={questionnaireResponse}
        mutationOptions={mutationOptions}
        valueFn={(qr) =>
          (qr.getAnswerValue(item.linkId) ?? null) as string | undefined
        }
        patchFn={(qr, value) => {
          qr.setAnswer(item, value ?? undefined)
        }}
        render={({ field: { value, onChange, disabled, ...rest } }) => (
          <div className={cn('grid', className)}>
            {!!label && (
              <Label
                className={cn('pb-1', {
                  'cursor-not-allowed': disabled,
                })}
              >
                {label}
              </Label>
            )}
            <ComboboxButton
              options={[
                ...getAnswerOptions(item),
                {
                  value: COMBOBOX_UNDEFINED_VALUE,
                  label: COMBOBOX_UNDEFINED_VALUE,
                },
              ]}
              value={value ?? COMBOBOX_UNDEFINED_VALUE}
              onChange={(value) =>
                onChange(value !== COMBOBOX_UNDEFINED_VALUE ? value : null)
              }
              variant="underline"
              disabled={disabled}
              className={cn({ 'pt-0': !!label })}
              {...rest}
            />
          </div>
        )}
      />
    )
  }

  if (item.type === 'boolean') {
    return (
      <ResourceFieldForm
        instance={questionnaireResponse}
        mutationOptions={mutationOptions}
        valueFn={(qr) =>
          (qr.getAnswerValue(item.linkId) ?? null) as boolean | undefined
        }
        patchFn={(qr, value) => {
          qr.setAnswer(item, value ?? undefined)
        }}
        render={({ field: { value, onChange, disabled, ...rest } }) => (
          <div className={cn('grid', className)}>
            {!!label && (
              <Label
                className={cn('pb-1', {
                  'cursor-not-allowed': disabled,
                })}
              >
                {label}
              </Label>
            )}
            <ComboboxButton
              options={[
                { value: 'Yes', label: 'Yes' },
                { value: 'No', label: 'No' },
                {
                  value: COMBOBOX_UNDEFINED_VALUE,
                  label: COMBOBOX_UNDEFINED_VALUE,
                },
              ]}
              value={match(value)
                .with(true, () => 'Yes' as const)
                .with(false, () => 'No' as const)
                .otherwise(() => COMBOBOX_UNDEFINED_VALUE)}
              onChange={(value) =>
                onChange(
                  value === COMBOBOX_UNDEFINED_VALUE ? null : value === 'Yes'
                )
              }
              variant="underline"
              disabled={disabled}
              className={cn({ 'pt-0': !!label })}
              {...rest}
            />
          </div>
        )}
      />
    )
  }

  // TODO: implement other item types

  return (
    <div className={cn('grid', className)}>
      <Label>{label}</Label>
      <Button disabled variant="underline">
        Not implemented: {item.type}
      </Button>
    </div>
  )
}
